import React from 'react';
import PropTypes from 'prop-types';
import IconButton from '@mui/joy/IconButton';
import PrintIcon from '@mui/icons-material/Print';

const DossierPrintButton = ({ dossierRef, title = 'Dossier' }) => {
    const handlePrint = () => {
        if (!dossierRef || !dossierRef.current) {
            return;
        }
        
        const originalTitle = document.title;
        document.title = title;
        
        const styleElement = document.createElement('style');
        styleElement.innerHTML = `
            @media print {
                /* Hide everything first */
                body * {
                    visibility: hidden;
                }
                
                /* Show only the dossier content */
                .dossier-print-area,
                .dossier-print-area * {
                    visibility: visible;
                }
                
                /* Position and size the dossier for full page */
                .dossier-print-area {
                    position: fixed !important;
                    left: 50% !important;
                    top: 0 !important;
                    transform: translateX(-50%) !important;
                    width: calc(100vw - 0.5in) !important;
                    max-width: none !important;
                    margin: 0 !important;
                    padding: 0.25in !important;
                }
                
                /* Preserve the Sheet and Card structure */
                .dossier-print-area .MuiSheet-root {
                    width: 100% !important;
                    max-width: none !important;
                    background: white !important;
                }
                
                .dossier-print-area .MuiCard-root {
                    width: 100% !important;
                    max-width: none !important;
                }
                
                /* Preserve Grid layout - keep columns */
                .dossier-print-area .MuiGrid-container {
                    width: 100% !important;
                    display: flex !important;
                    flex-wrap: wrap !important;
                }
                
                .dossier-print-area .MuiGrid-item {
                    display: block !important;
                }
                
                /* Preserve the 2-column layout for xs=6 items */
                .dossier-print-area .MuiGrid-grid-xs-6 {
                    flex-basis: 50% !important;
                    max-width: 50% !important;
                }
                
                /* Full width for xs=12 items */
                .dossier-print-area .MuiGrid-grid-xs-12 {
                    flex-basis: 100% !important;
                    max-width: 100% !important;
                }
                
                /* Reduce spacing under InfoLine components */
                .dossier-print-area [id="date"],
                .dossier-print-area [id="observedBy"],
                .dossier-print-area [id="location"],
                .dossier-print-area [id="town"],
                .dossier-print-area [id="quality"],
                .dossier-print-area [id="substantiation"],
                .dossier-print-area [id="keywords"],
                .dossier-print-area [id="event"],
                .dossier-print-area [id="latLong"],
                .dossier-print-area [id="substrate"],
                .dossier-print-area [id="locDeterminization"],
                .dossier-print-area [id="isDetected"],
                .dossier-print-area [id="associatedObservations"],
                .dossier-print-area [id="observationRestriction"],
                .dossier-print-area [id="subject"],
                .dossier-print-area [id="synonym"],
                .dossier-print-area [id="reviewedBy"] {
                    margin-bottom: 4px !important;
                }
                
                .dossier-print-area [id="date"] .MuiStack-root,
                .dossier-print-area [id="observedBy"] .MuiStack-root,
                .dossier-print-area [id="location"] .MuiStack-root,
                .dossier-print-area [id="town"] .MuiStack-root,
                .dossier-print-area [id="quality"] .MuiStack-root,
                .dossier-print-area [id="substantiation"] .MuiStack-root,
                .dossier-print-area [id="keywords"] .MuiStack-root,
                .dossier-print-area [id="event"] .MuiStack-root,
                .dossier-print-area [id="latLong"] .MuiStack-root,
                .dossier-print-area [id="substrate"] .MuiStack-root,
                .dossier-print-area [id="locDeterminization"] .MuiStack-root,
                .dossier-print-area [id="isDetected"] .MuiStack-root,
                .dossier-print-area [id="associatedObservations"] .MuiStack-root,
                .dossier-print-area [id="observationRestriction"] .MuiStack-root,
                .dossier-print-area [id="subject"] .MuiStack-root,
                .dossier-print-area [id="synonym"] .MuiStack-root,
                .dossier-print-area [id="reviewedBy"] .MuiStack-root {
                    margin-bottom: 4px !important;
                }
                
                /* Fix notes section spacing and wrapping */
                .dossier-print-area .MuiCard-root .MuiGrid-container {
                    margin-bottom: 0 !important;
                }
                
                .dossier-print-area .MuiCard-root .MuiGrid-item {
                    padding: 4px 0 !important;
                    word-break: break-word !important;
                    overflow-wrap: break-word !important;
                }
                
                /* Ensure note labels and text have proper spacing */
                .dossier-print-area .MuiGrid-grid-xs-3 {
                    flex-basis: 35% !important;
                    max-width: 35% !important;
                    padding-right: 12px !important;
                }
                
                .dossier-print-area .MuiGrid-grid-xs-9 {
                    flex-basis: 65% !important;
                    max-width: 65% !important;
                    word-break: break-word !important;
                    white-space: pre-wrap !important;
                    overflow-wrap: break-word !important;
                }
                
                /* Hide action buttons */
                .dossier-action-buttons,
                .dossier-print-button {
                    display: none !important;
                }
                
                /* Page setup */
                @page {
                    size: letter;
                    margin: 0;
                }
                
                body {
                    margin: 0 !important;
                    padding: 0 !important;
                }
            }
        `;
        
        document.head.appendChild(styleElement);
        
        if (dossierRef && dossierRef.current) {
            dossierRef.current.classList.add('dossier-print-area');
        }
        
        window.print();
        
        setTimeout(() => {
            document.title = originalTitle;
            if (styleElement && styleElement.parentNode) {
                document.head.removeChild(styleElement);
            }
            if (dossierRef && dossierRef.current) {
                dossierRef.current.classList.remove('dossier-print-area');
            }
        }, 1000);
    };

    return (
        <IconButton
            className="dossier-print-button"
            variant="outlined"
            color="neutral"
            size="sm"
            onClick={handlePrint}
            title="Print Dossier"
        >
            <PrintIcon />
        </IconButton>
    );
};

DossierPrintButton.propTypes = {
    dossierRef: PropTypes.object.isRequired,
    title: PropTypes.string
};

export default DossierPrintButton;